import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { getStatusMessage } from '../../utils/response-code-messages';
import Snackbar from '../../common/snackBar';
import get from 'lodash/get';
import { menuList } from '../../utils/route-paths';

const page_name = 'useCaseBuilder';

interface UseCaseBuilderRequestActionTypes {
  type?: string;
  payload?: {
    res?: {
      search_item?: string;
      data?: Array<any>;
      total_count?: number;
      page?: number;
    };
  };
}

interface UseCase {
  id: number;
  use_case_name: string;
  use_case_category: string;
  use_case_type: string;
  application_name?: string;
  application_url?: string;
  component_name?: string;
  navigation_name?: string;
  navigation_slug?: string;
  navigation_icon?: any;
  json_template?: string;
  custom_schema?: string;
  onto_manage_id?: number;
  assets_list?: string[];
  variable_mapping?: any[];
  created_date: string;
  status?: string;
  grafana_id?: any;
  grafana_uid?: any;
  user_access_config?: any;
  use_case_id?: string | number;
  isdefault?: boolean;
  default_dashboard_users?: string[];
  last_selected_onto_manage_id?: number;
}

interface Template {
  id: number;
  templateName: string;
  createdBy: string;
  created_date: string;
}

interface CustomJsonTemplate {
  template_id: string | number;
  template_name: string;
  template_schema: string; // JSON content as a string
  created_date: string;
  selected_template: any;
}

export interface UseCaseBuilderState {
  isUseCaseDataLoading: boolean;
  useCaseData: Array<UseCase>;
  useCaseDataTotalRowCount: number;
  useCaseDataPageNumber: number;
  useCaseDataError: boolean;
  isAddUseCaseLoading: boolean;
  isAddUseCaseSuccess: boolean;
  isAddUseCaseError: boolean;
  isUpdateUseCaseLoading: boolean;
  isUpdateUseCaseSuccess: boolean;
  isUpdateUseCaseError: boolean;
  isRemoveUseCaseLoading: boolean;
  isRemoveUseCaseSuccess: boolean;
  isRemoveUseCaseError: boolean;
  useCaseMessage: string;
  searchItem: undefined | string;
  customJsonTemplates: Array<CustomJsonTemplate>;
  isCustomJsonTemplateLoading: boolean;
  isCustomJsonTemplateError: boolean;
  mergedMenuData: any[];
  isAddCustomJsonTemplateSuccess: boolean;
  isAddCustomJsonTemplateError: boolean;
  isUpdateCustomJsonTemplateSuccess: boolean;
  isUpdateCustomJsonTemplateError: boolean;
  isDeleteCustomJsonTemplateSuccess: boolean;
  isDeleteCustomJsonTemplateError: boolean;
  customJsonTemplateTotalRowCount: number;
  customJsonTemplatePageNumber: number;
  isApproveUseCaseLoading: boolean;
  isApproveUseCaseSuccess: boolean;
  isApproveUseCaseError: boolean;
  deployedUsecaseData: Array<UseCase>;
  deployedUsecaseTotalRowCount: number;
  deployedUsecasePageNumber: number;
  isDeployedUsecaseLoading: boolean;
  isDeployedUsecaseError: boolean;
  statusType: string;
  isSetDefaultDashboardSuccess: boolean;
  isSetDefaultDashboardError: boolean;
  grafanaTemplateData :any;
  isGrafanaTemplateError: boolean;
  isGrafanaTemplateLoading: boolean;
  isForcedDelete?: boolean;
}

const initialState: UseCaseBuilderState = {
  isUseCaseDataLoading: false,
  useCaseData: [],
  useCaseDataTotalRowCount: 0,
  useCaseDataPageNumber: 1,
  useCaseDataError: false,
  isAddUseCaseLoading: false,
  isAddUseCaseSuccess: false,
  isAddUseCaseError: false,
  isUpdateUseCaseLoading: false,
  isUpdateUseCaseSuccess: false,
  isUpdateUseCaseError: false,
  isRemoveUseCaseLoading: false,
  isRemoveUseCaseSuccess: false,
  isRemoveUseCaseError: false,
  useCaseMessage: '',
  searchItem: '',
  customJsonTemplates: [],
  isCustomJsonTemplateLoading: false,
  isCustomJsonTemplateError: false,
  mergedMenuData: [],
  isAddCustomJsonTemplateSuccess: false,
  isAddCustomJsonTemplateError: false,
  isUpdateCustomJsonTemplateSuccess: false,
  isUpdateCustomJsonTemplateError: false,
  isDeleteCustomJsonTemplateSuccess: false,
  isDeleteCustomJsonTemplateError: false,
  customJsonTemplateTotalRowCount: 0,
  customJsonTemplatePageNumber: 1,
  isApproveUseCaseLoading: false,
  isApproveUseCaseSuccess: false,
  isApproveUseCaseError: false,
  deployedUsecaseData: [],
  deployedUsecaseTotalRowCount: 0,
  deployedUsecasePageNumber: 1,
  isDeployedUsecaseLoading: false,
  isDeployedUsecaseError: false,
  statusType: '',
  isSetDefaultDashboardSuccess: false,
  isSetDefaultDashboardError: false,
  grafanaTemplateData:[],
  isGrafanaTemplateError: false,
  isGrafanaTemplateLoading: false,
  isForcedDelete: false
};

const useCaseBuilderReducer = createSlice({
  name: page_name,
  initialState: initialState,
  reducers: {
    getUseCaseDataRequest: (state, action: UseCaseBuilderRequestActionTypes) => {
      state.isUseCaseDataLoading = true;
      state.isAddUseCaseSuccess = false;
      state.isUpdateUseCaseSuccess = false;
      state.isRemoveUseCaseSuccess = false;
    },
    getUseCaseDataResponse: (state, action) => {
      state.isUseCaseDataLoading = false;
      // if (state.searchItem === get(action, 'payload.search_item', '')) {
      // Extract data from the payload
      const useCaseData = get(action, 'payload.data.data', []) as Array<UseCase>;
      const totalRowCount = get(action, 'payload.data.total_count', 0);
      const pageNumber = get(action, 'payload.data.page', 0);

      // Update state with the extracted data
      state.useCaseData = useCaseData;
      state.useCaseDataTotalRowCount = totalRowCount;
      state.useCaseDataPageNumber = pageNumber;
      // }
    },
    getUseCaseDataFailed: (state, action: any) => {
      state.isUseCaseDataLoading = false;
      state.useCaseDataError = true;
      state.useCaseData = [];
      const message = getStatusMessage(page_name, action.payload);
      state.useCaseMessage = message;
    },
    getDeployedUsecaseRequest: (state) => {
      state.isDeployedUsecaseLoading = true;
      state.isDeployedUsecaseError = false;
    },
    getDeployedUsecaseResponse: (state, action) => {
      state.isDeployedUsecaseLoading = false;
      try {
        const deployedUsecaseData = get(action, 'payload.data.data', []) as Array<UseCase>;
        const totalRowCount = get(action, 'payload.data.total_count', 0);
        const pageNumber = get(action, 'payload.data.page', 0);

        state.deployedUsecaseData = deployedUsecaseData;
        state.deployedUsecaseTotalRowCount = totalRowCount;
        state.deployedUsecasePageNumber = pageNumber;

        // Deep clone menuList to avoid mutating the original
        const groupedMenuList = JSON.parse(JSON.stringify(menuList));

        // Find Dashboard and Applications groups
        const dashboardGroup = groupedMenuList.find((item: any) => item.type === 'dashboard');
        const applicationsGroup = groupedMenuList.find((item: any) => item.type === 'application');

        // Prepare dashboard and application menu items
        const dashboardItems = deployedUsecaseData
          .filter((useCase: UseCase) => useCase.status === 'deployed' && useCase.use_case_category === 'dashboard')
          .map((useCase: UseCase, idx: number) => ({
            id: 1000 + idx, // Unique id, adjust as needed
            title: useCase.navigation_name,
            isDefault: useCase.isdefault,
            path: '/dashboards/' + useCase.navigation_slug,
            Icon: JSON.parse(useCase?.navigation_icon)?.svgContent,
            endPointPath: 'auth/users',
            route_id: useCase.application_name + '_dashboard_' + idx,
            type: useCase.use_case_category,
            hidden: false,
            applicationUrl: useCase.application_url,
            applicationName: useCase.application_name,
            componentName: useCase.component_name,
            jsonTemplate: useCase.json_template,
            customSchema: useCase.custom_schema,
            ontologySelection: useCase.onto_manage_id,
            assetsList: useCase.assets_list,
            variableMapping: useCase.variable_mapping,
            created_date: useCase.created_date,
            grafana_id: useCase.grafana_id,
            grafana_uid: useCase.grafana_uid,
            user_access_config: useCase.user_access_config,
            isSubMenu: true,
            use_case_id: useCase.use_case_id,
            use_case_type : useCase.use_case_type,
              default_dashboard_users: useCase.default_dashboard_users,
              last_selected_onto_manage_id: useCase.last_selected_onto_manage_id
          }));

        const applicationItems = deployedUsecaseData
          .filter((useCase: UseCase) => useCase.status === 'deployed' && useCase.use_case_category === 'application')
          .map((useCase: UseCase, idx: number) => ({
            id: 2000 + idx, // Unique id, adjust as needed
            title: useCase.navigation_name,
            path: '/applications/' + useCase.navigation_slug,
            Icon: JSON.parse(useCase?.navigation_icon)?.svgContent,
            endPointPath: 'auth/users',
            route_id: useCase.application_name + '_application_' + idx,
            type: useCase.use_case_category,
            hidden: false,
            applicationUrl: useCase.application_url,
            applicationName: useCase.application_name,
            componentName: useCase.component_name,
            jsonTemplate: useCase.json_template,
            customSchema: useCase.custom_schema,
            ontologySelection: useCase.onto_manage_id,
            assetsList: useCase.assets_list,
            variableMapping: useCase.variable_mapping,
            created_date: useCase.created_date,
            grafana_id: useCase.grafana_id,
            grafana_uid: useCase.grafana_uid,
            user_access_config: useCase.user_access_config,
            isSubMenu: true,
            use_case_type : useCase.use_case_type,
            default_dashboard_users: useCase.default_dashboard_users,
            last_selected_onto_manage_id: useCase.last_selected_onto_manage_id,
          }));

        // Add to Dashboard group
        if (dashboardGroup && Array.isArray(dashboardGroup.children)) {
          dashboardGroup.children = [...dashboardGroup.children, ...dashboardItems];
        }

        // Add to Applications group
        if (applicationsGroup && Array.isArray(applicationsGroup.children)) {
          applicationsGroup.children = [...applicationsGroup.children, ...applicationItems];
        }

        // Set mergedMenuData to the updated groupedMenuList
        state.mergedMenuData = groupedMenuList;
      } catch (error) {
        state.deployedUsecaseData = [];
        state.isDeployedUsecaseError = true;
        state.mergedMenuData = menuList;
        state.useCaseMessage = 'An error occurred while processing deployed use case data.';
      }
    },
    getDeployedUsecaseFailed: (state, action) => {
      state.isDeployedUsecaseLoading = false;
      state.isDeployedUsecaseError = true;
      state.deployedUsecaseData = [];
      state.mergedMenuData = menuList;
      const message = getStatusMessage(page_name, action.payload);
      state.useCaseMessage = message;
      // Snackbar.error(message);
    },
    addUseCaseRequest: (state) => {
      state.isUseCaseDataLoading = true;
      state.isAddUseCaseLoading = true;
      state.isAddUseCaseSuccess = false;
      state.isAddUseCaseError = false;
    },
    addUseCaseResponse: (state, action) => {
      state.isUseCaseDataLoading = false;
      state.isAddUseCaseLoading = false;
      state.isAddUseCaseSuccess = true;
      const message = getStatusMessage(page_name, action?.payload?.code);
      state.useCaseMessage = message;
      Snackbar.success(message);
    },
    addUseCaseFailed: (state, action: any) => {
      state.isUseCaseDataLoading = false;
      state.isAddUseCaseLoading = false;
      state.isAddUseCaseError = true;

      const message = getStatusMessage(page_name, action.payload);
      state.useCaseMessage = message;
      Snackbar.error(message);
    },
    updateUseCaseRequest: (state, action: any) => {
      state.isUseCaseDataLoading = true;
      state.isUpdateUseCaseLoading = true;
      state.isUpdateUseCaseSuccess = false;
      state.isUpdateUseCaseError = false;
      state.statusType = get(action, 'payload.status', '');
    },
    updateUseCaseResponse: (state, action: any) => {
      state.isUseCaseDataLoading = false;
      state.isUpdateUseCaseLoading = false;
      state.isUpdateUseCaseSuccess = true;
      if (state.statusType === 'waiting_for_approval') {
        state.useCaseMessage = 'Usecase submitted for approval successfully!';
        Snackbar.success('Usecase submitted for approval successfully!');
      } else if (state.statusType === 'update_access') {
        state.useCaseMessage = 'Access details updated successfully!';
        Snackbar.success(state.useCaseMessage);
      } else {
        const message = getStatusMessage(page_name, action?.payload?.code);
        state.useCaseMessage = message;
        Snackbar.success(message);
      }
      state.statusType = '';
    },
    updateUseCaseFailed: (state, action) => {
      state.isUseCaseDataLoading = false;
      state.isUpdateUseCaseLoading = false;
      state.isUpdateUseCaseError = true;
      state.isUpdateUseCaseSuccess = false;
      if (state.statusType === 'waiting_for_approval') {
        state.useCaseMessage = 'Something went wrong while submitting usecase for approval!';
        Snackbar.error('Something went wrong while submitting usecase for approval!');
      } else if (state.statusType === 'update_access') {
        state.useCaseMessage = 'Something went wrong while submitting usecase for approval!';
        Snackbar.error(state.useCaseMessage);
      } else {
        const message = getStatusMessage(page_name, action.payload);
        state.useCaseMessage = message;
        Snackbar.error(message);
      }
      state.statusType = '';
    },
    updateUseCaseAccessRequest: (state, action: any) => {
      state.isUseCaseDataLoading = true;
      state.isUpdateUseCaseLoading = true;
      state.isUpdateUseCaseSuccess = false;
      state.isUpdateUseCaseError = false;
      state.statusType = 'update_access';
    },
    updateUseCaseAccessResponse: (state, action: any) => {
      state.isUseCaseDataLoading = false;
      state.isUpdateUseCaseLoading = false;
      state.isUpdateUseCaseSuccess = true;
      state.useCaseMessage = 'Access details updated successfully!';
      Snackbar.success(state.useCaseMessage);
      state.statusType = '';
    },
    updateUseCaseAccessFailed: (state, action: any) => {
      state.isUseCaseDataLoading = false;
      state.isUpdateUseCaseLoading = false;
      state.isUpdateUseCaseError = true;
      state.isUpdateUseCaseSuccess = false;
      state.useCaseMessage = 'Something went wrong while updating access details!';
      Snackbar.error(state.useCaseMessage);
      state.statusType = '';
    },
    removeUseCaseRequest: (state, action) => {
      state.isRemoveUseCaseLoading = true;
      state.isUseCaseDataLoading = true;
      state.isRemoveUseCaseSuccess = false;
      state.isRemoveUseCaseError = false;
      state.isForcedDelete = false;
    },
    removeUseCaseResponse: (state, action) => {
      state.isRemoveUseCaseLoading = false;
      state.isRemoveUseCaseSuccess = true;
      state.isUseCaseDataLoading = false;
      state.isRemoveUseCaseError = false;
      state.isForcedDelete = false;
      const message = getStatusMessage(page_name, action?.payload?.status);
      state.useCaseMessage = message;
      Snackbar.success(message);
    },
    removeUseCaseFailed: (state, action) => {
      state.isRemoveUseCaseLoading = false;
      state.isRemoveUseCaseError = true;
      state.isUseCaseDataLoading = false;
      state.isRemoveUseCaseSuccess = false;
      state.isForcedDelete = get(action.payload, 'data[0].forced_delete', false);
      // const message = getStatusMessage(page_name, action.payload?.status);
      // state.useCaseMessage = message; error?.response?.data
      // Snackbar.error(message);
    },
    // Add methods to handle approve use case
    approveUseCaseRequest: (state, action: any) => {
      state.isUseCaseDataLoading = true;
      state.isApproveUseCaseLoading = true;
      state.isApproveUseCaseSuccess = false;
      state.isApproveUseCaseError = false;
      state.statusType = get(action, 'payload.status', '');
    },
    approveUseCaseResponse: (state, action) => {
      state.isUseCaseDataLoading = false;
      state.isApproveUseCaseLoading = false;
      state.isApproveUseCaseSuccess = true;


      if (state.statusType === 'approved') {
        state.useCaseMessage = 'Usecase approved successfully!';
        Snackbar.success('Usecase approved successfully!');
      } else if (state.statusType === 'rejected') {
        state.useCaseMessage = 'Usecase rejected successfully!';
        Snackbar.error('Usecase rejected successfully!');
      } else {
        const message = getStatusMessage(page_name, action?.payload?.status);
        state.useCaseMessage = message;
        Snackbar.success(message);
      }
      state.statusType = '';
    },
    approveUseCaseFailed: (state, action) => {
      state.isUseCaseDataLoading = false;
      state.isApproveUseCaseLoading = false;
      state.isApproveUseCaseError = true;

      if (state.statusType === 'approved') {
        state.useCaseMessage = 'Something went wrong while approving usecase!';
        Snackbar.success('Something went wrong while approving usecase!');
      } else if (state.statusType === 'rejected') {
        state.useCaseMessage = 'Something went wrong while rejecting usecase!';
        Snackbar.error('Something went wrong while rejecting usecase!');
      } else {
        const message = getStatusMessage(page_name, action.payload);
        state.useCaseMessage = message;
        Snackbar.error(message);
      }
      state.statusType = '';
    },
    resetLoadingState: (state) => {
      state.isAddUseCaseSuccess = false;
      state.isUpdateUseCaseError = false;
      state.isUpdateUseCaseSuccess = false;
      state.isAddUseCaseError = false;
      state.isRemoveUseCaseSuccess = false;
      state.isRemoveUseCaseError = false;
      state.useCaseMessage = '';
      state.useCaseDataError = false;
    },
    resetUseCaseData: (state) => {
      state.useCaseData = [];
      state.useCaseDataTotalRowCount = 0;
      state.useCaseDataPageNumber = 1;
      state.searchItem = '';
    },
    onSearchForItem: (state, action) => {
      state.searchItem = action.payload;
    },
    resetStatusType: (state) => {
      state.statusType = '';
    },
    // Get Custom JSON Templates
    getCustomJsonTemplatesRequest: (state) => {
      state.isUseCaseDataLoading = true;
      state.isCustomJsonTemplateError = false;
    },
    getCustomJsonTemplatesResponse: (state, action: any) => {
      state.isUseCaseDataLoading = false;
      // if (state.searchItem === get(action, 'payload.search_item', '')) {
      state.customJsonTemplates = get(action, 'payload.data.data', '');
      state.customJsonTemplateTotalRowCount = get(action, 'payload.data.total_count', '');
      state.customJsonTemplatePageNumber = get(action, 'payload.data.page', '');
      // }
    },
    getCustomJsonTemplatesFailed: (state, action) => {
      state.isUseCaseDataLoading = false;
      state.isCustomJsonTemplateError = true;
      state.customJsonTemplates = [];
      const message = getStatusMessage('customJsonTemplate', action.payload);
      state.useCaseMessage = message;
      // Snackbar.error(message);
    },
    getGrafanaTemplatesRequest: (state) => {
      state.isUseCaseDataLoading = true;
      state.isGrafanaTemplateLoading = true;
      state.isGrafanaTemplateError = false;
    },
    getGrafanaTemplatesResponse: (state, action: any) => {
      state.isUseCaseDataLoading = false;
      state.isGrafanaTemplateLoading = false;
      state.isGrafanaTemplateError = false;
      state.grafanaTemplateData = get(action, 'payload.data.data', '');
    },
    getGrafanaTemplatesFailed: (state, action) => {
      state.isUseCaseDataLoading = false;
      state.isGrafanaTemplateLoading = false;
      state.isGrafanaTemplateError = true;
      state.grafanaTemplateData = [];
    },
    addCustomJsonTemplateRequest: (state) => {
      state.isUseCaseDataLoading = true;
      state.isCustomJsonTemplateLoading = true;
      state.isAddCustomJsonTemplateSuccess = false;
      state.isAddCustomJsonTemplateError = false;
    },
    addCustomJsonTemplateResponse: (state, action: any) => {
      state.isUseCaseDataLoading = false;
      state.isCustomJsonTemplateLoading = false;
      state.isAddCustomJsonTemplateSuccess = true;
      const message = getStatusMessage('customJsonTemplate', action?.payload?.status);
      state.useCaseMessage = message;
      Snackbar.success(message);
    },
    addCustomJsonTemplateFailed: (state, action) => {
      state.isUseCaseDataLoading = false;
      state.isCustomJsonTemplateLoading = false;
      state.isAddCustomJsonTemplateError = true;

      const message = getStatusMessage('customJsonTemplate', action.payload);
      state.useCaseMessage = message;
      Snackbar.error(message);
    },
    updateCustomJsonTemplateRequest: (state) => {
      state.isUseCaseDataLoading = true;
      state.isCustomJsonTemplateLoading = true;
      state.isUpdateCustomJsonTemplateSuccess = false;
      state.isUpdateCustomJsonTemplateError = false;

    },
    updateCustomJsonTemplateResponse: (state, action: any) => {
      state.isUseCaseDataLoading = false;
      state.isCustomJsonTemplateLoading = false;
      state.isUpdateCustomJsonTemplateSuccess = true;
      const updatedTemplate = action.payload;

      const message = getStatusMessage('customJsonTemplate', action?.payload?.status);
      state.useCaseMessage = message;
      Snackbar.success(message);
    },
    updateCustomJsonTemplateFailed: (state, action) => {
      state.isUseCaseDataLoading = false;
      state.isCustomJsonTemplateLoading = false;
      state.isUpdateCustomJsonTemplateError = true;

      const message = getStatusMessage('customJsonTemplate', action.payload);
      state.useCaseMessage = message;
      Snackbar.error(message);
    },
    deleteCustomJsonTemplateRequest: (state) => {
      state.isUseCaseDataLoading = true;
      state.isCustomJsonTemplateLoading = true;
      state.isDeleteCustomJsonTemplateSuccess = false;
      state.isDeleteCustomJsonTemplateError = false;
      state.isForcedDelete = false;
    },
    deleteCustomJsonTemplateResponse: (state, action: any) => {
      state.isUseCaseDataLoading = false;
      state.isCustomJsonTemplateLoading = false;
      state.isDeleteCustomJsonTemplateSuccess = true;
      state.isForcedDelete = false;
      state.customJsonTemplates = state.customJsonTemplates.filter((template) => template.template_id !== action.payload);
      const message = getStatusMessage('customJsonTemplate', action?.payload?.status);
      state.useCaseMessage = message;
      Snackbar.success(message);
    },
    deleteCustomJsonTemplateFailed: (state, action) => {
      state.isUseCaseDataLoading = false;
      state.isCustomJsonTemplateLoading = false;
      state.isDeleteCustomJsonTemplateError = true;
      // state.isForcedDelete = true;
      state.isForcedDelete = get(action.payload, 'data[0].forced_delete', false);
      // const message = getStatusMessage('customJsonTemplate', action.payload);
      // state.useCaseMessage = message;
      // Snackbar.error(message);
    },
    setDefaultDashboardRequest: (state) => {
      state.isUseCaseDataLoading = true;
      state.useCaseMessage = '';
      state.isSetDefaultDashboardSuccess = false;
      state.isSetDefaultDashboardError = false;
    },
    setDefaultDashboardResponse: (state, action: PayloadAction<{ navigation_slug: string }>) => {
      state.isUseCaseDataLoading = false;
      state.isSetDefaultDashboardSuccess = true;
      state.isSetDefaultDashboardError = false;
      // state.useCaseMessage = 'Default dashboard updated successfully!';
      // Snackbar.success(state.useCaseMessage);
    },
    setDefaultDashboardFailed: (state, action) => {
      state.isUseCaseDataLoading = false;
      state.isSetDefaultDashboardSuccess = false;
      state.isSetDefaultDashboardError = true;
      const message = getStatusMessage(page_name, action.payload);
      state.useCaseMessage = message;
      Snackbar.error(message);
    },
    clearForcedDelete: (state) => {
      state.isForcedDelete = false;
    },
    updateUseCaseOntologyFilterRequest: (state, action) => {},
    updateUseCaseOntologyFilterResponse: (state, action) => {
      const modifiedUsecase = get(action, 'payload.data.data', null);
      if (!modifiedUsecase) return;
      const dashboardItems = state.deployedUsecaseData.map((usecase) => {
        if (usecase.use_case_id === modifiedUsecase.use_case_id) {
          return {
            ...usecase,
            ...modifiedUsecase,
          };
        }
        return usecase;
      });
      state.deployedUsecaseData = dashboardItems;
    },
    updateUseCaseOntologyFilterFailed: (state, action) => {},
  },
});

export const useCaseBuilderActions = useCaseBuilderReducer.actions;
export default useCaseBuilderReducer;